/**
 * Client Status Types and Utilities
 * Centralizes client status-related interfaces and Element UI integration
 */

/**
 * Client status enumeration
 * Defines all possible client status values used across the application
 */
export enum ClientStatus {
	NEW = 'Nuovo',
	MODIFIED = 'Modificato',
	DELETED = 'Eliminato',
}

/**
 * Element UI tag types for styling
 * Used for el-tag type attribute
 */
export type TagType = 'success' | 'info' | 'warning' | 'danger' | '';

/**
 * Raw client status value (from API/database)
 * Can be string, null, or undefined
 */
export type ClientStatusValue = string | null | undefined;

/**
 * Client status utilities for processing and styling
 */
export class ClientStatusUtils {
	/**
	 * Get Element UI tag type based on client status
	 * Maps status strings to appropriate Element UI tag colors
	 */
	static getTagType(status: ClientStatusValue): TagType {
		if (!status) {
			return '';
		}

		switch (status.toLowerCase()) {
			case 'nuovo':
				return 'success';
			case 'modificato':
				return 'warning';
			case 'eliminato':
				return 'danger';
			default:
				return 'info';
		}
	}

	/**
	 * Check if status is valid client status
	 */
	static isValidStatus(status: unknown): status is string {
		return typeof status === 'string' && status.trim().length > 0;
	}

	/**
	 * Get normalized status value
	 * Ensures consistent casing and format
	 */
	static normalizeStatus(status: ClientStatusValue): string {
		if (!this.isValidStatus(status)) {
			return '';
		}

		const normalized = status.toLowerCase();
		switch (normalized) {
			case 'nuovo':
				return ClientStatus.NEW;
			case 'modificato':
				return ClientStatus.MODIFIED;
			case 'eliminato':
				return ClientStatus.DELETED;
			default:
				return status; // Return original if not recognized
		}
	}

	/**
	 * Format status for display (same as formatters mixin)
	 * Maintains compatibility with existing formatClientStatus
	 */
	static formatForDisplay(status: ClientStatusValue): string {
		return status || '-';
	}
}

/**
 * Client status validation helpers
 */
export const ClientStatusValidators = {
	/**
	 * Check if status is NEW
	 */
	isNew: (status: ClientStatusValue): boolean => {
		return status?.toLowerCase() === 'nuovo';
	},

	/**
	 * Check if status is MODIFIED
	 */
	isModified: (status: ClientStatusValue): boolean => {
		return status?.toLowerCase() === 'modificato';
	},

	/**
	 * Check if status is DELETED
	 */
	isDeleted: (status: ClientStatusValue): boolean => {
		return status?.toLowerCase() === 'eliminato';
	},

	/**
	 * Check if status represents an active state (not deleted)
	 */
	isActive: (status: ClientStatusValue): boolean => {
		return !ClientStatusValidators.isDeleted(status);
	},
} as const;
